<template>
  <div class="audit-dashboard pa-4">
    <v-card flat class="border-light p-2">
      <v-data-table
        :headers="loanHeaders"
        :items="auditLogs"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        class="custom-audit-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50],
          'show-first-last-page': true
        }"
      >
        <!-- Case/Loan No Column -->
        <template v-slot:[`item.loanNo`]="{ item }">
          <a
            class="text-body-2 font-weight-bold primary--text text-decoration-none"
            @click="showDetails(item)"
          >
            {{ item.loanNo }}
          </a>
        </template>

        <!-- Action Column -->
        <template v-slot:[`item.action`]="{ item }">
          <div class="d-flex align-center">
            <v-chip
              small
              label
              :color="getActionColor(item.action)"
              dark
              class="font-weight-bold text-uppercase mr-2"
            >
              {{ item.action }}
            </v-chip>
            <span class="text-caption font-weight-bold grey--text">
              {{ getAuditActionName(item) }}
            </span>
          </div>
        </template>

        <!-- User Column -->
        <template v-slot:[`item.whoChanged`]="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="24" color="grey lighten-2" class="mr-2">
              <span class="grey--text text--darken-2 text-caption">
                {{ (item.whoChanged?.firstName || '?')[0] }}
              </span>
            </v-avatar>
            <span class="text-body-2 font-weight-medium"
              >{{ item.whoChanged?.firstName || 'System' }}
              {{ item.whoChanged?.lastName || '' }}</span
            >
          </div>
        </template>

        <!-- Timestamp Column -->
        <template v-slot:[`item.createdAt`]="{ item }">
          <span class="text-body-2">{{ formatDate(item.createdAt) }}</span>
        </template>

        <!-- Remarks Column -->
        <template v-slot:[`item.remarks`]="{ item }">
          <span class="text-caption grey--text">
            {{ item.remarks || '-' }}
          </span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="1100px" scrollable>
      <v-card v-if="selectedLoan" class="p-2">
        <v-card-title class="grey lighten-4 py-3 px-4">
          <v-icon left color="primary">mdi-camera-account</v-icon>
          Audit Summary & Evidence: {{ selectedLoan.loanNo }}
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4 bg-white">
          <!-- Case Summary Section (No Background) -->
          <div class="mb-6 pa-4 rounded border">
            <div class="d-flex align-center justify-space-between mb-4">
              <span class="text-h6 font-weight-black primary--text">Case Summary</span>
              <v-chip small color="primary" label class="font-weight-bold">
                {{ selectedLoan.loanNo }}
              </v-chip>
            </div>
            <v-row dense>
              <v-col cols="12" sm="3">
                <div class="text-caption grey--text font-weight-bold">Customer</div>
                <div class="text-body-2 font-weight-black">{{ getCustomerName(selectedLoan) }}</div>
              </v-col>
              <v-col cols="12" sm="3">
                <div class="text-caption grey--text font-weight-bold">Current Status</div>
                <div class="text-body-2">
                  <v-chip
                    x-small
                    label
                    :color="getActionColor(selectedLoan.lastAction)"
                    dark
                    class="font-weight-black"
                  >
                    {{ selectedLoan.lastAction }}
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="12" sm="3">
                <div class="text-caption grey--text font-weight-bold">Last Action By</div>
                <div class="text-body-2 font-weight-black">
                  {{ selectedLoan.whoChanged?.firstName }} {{ selectedLoan.whoChanged?.lastName }}
                </div>
              </v-col>
              <v-col cols="12" sm="3">
                <div class="text-caption grey--text font-weight-bold">Last Updated</div>
                <div class="text-body-2 font-weight-black">
                  {{ formatDate(selectedLoan.lastUpdatedAt) }}
                </div>
              </v-col>
            </v-row>
          </div>

          <v-divider class="mb-6"></v-divider>

          <!-- Date Selection Chips -->
          <div v-if="availableDates.length > 0" class="mb-6">
            <div class="text-subtitle-2 font-weight-black mb-3 grey--text text--darken-2">
              Activity History by Date
            </div>
            <v-chip-group
              v-model="selectedDateIndex"
              mandatory
              color="primary"
              active-class="primary  elevation-2"
            >
              <v-chip
                v-for="(date, idx) in availableDates"
                :key="idx"
                outlined
                class="px-4 font-weight-bold"
              >
                <v-icon left x-small>mdi-calendar-clock</v-icon>
                {{ formatChipDate(date) }}
              </v-chip>
            </v-chip-group>
          </div>

          <!-- Data Comparison Section (No Background) -->
          <div v-if="selectedAuditLog" class="mb-8">
            <div class="d-flex align-center mb-6 pa-4 rounded border">
              <div class="mr-8">
                <div class="text-caption grey--text font-weight-bold">Action</div>
                <div
                  class="text-body-1 font-weight-black"
                  :class="`${getActionColor(selectedAuditLog.action)}--text`"
                >
                  {{ selectedAuditLog.action }}
                </div>
              </div>
              <div class="mr-8 border-l pl-8">
                <div class="text-caption grey--text font-weight-bold">Performed By</div>
                <div class="text-body-1 font-weight-black">
                  {{ selectedAuditLog.whoChanged?.firstName }}
                  {{ selectedAuditLog.whoChanged?.lastName }}
                </div>
              </div>
              <div class="border-l pl-8">
                <div class="text-caption grey--text font-weight-bold">Date & Time</div>
                <div class="text-body-1 font-weight-black">
                  {{ formatDate(selectedAuditLog.createdAt) }}
                </div>
              </div>
            </div>

            <!-- Structured Changes -->
            <div v-if="selectedAuditLog.action === 'UPDATE'" class="p-2">
              <div class="text-subtitle-2 font-weight-black mb-3">Data Comparison</div>
              <v-simple-table dense class="border rounded mb-6">
                <template v-slot:default>
                  <thead class="grey lighten-4">
                    <tr>
                      <th class="text-left font-weight-bold">Property</th>
                      <th class="text-left font-weight-bold">From Value</th>
                      <th class="text-left font-weight-bold">To Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="change in getParsedChanges(selectedAuditLog)" :key="change.key">
                      <td class="font-weight-medium text-caption">
                        {{ formatFieldName(change.key) }}
                      </td>
                      <td class="py-2">
                        <div class="diff-box old">{{ formatDisplayValue(change.from) }}</div>
                      </td>
                      <td class="py-2">
                        <div class="diff-box new">{{ formatDisplayValue(change.to) }}</div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
            <div v-else>
              <div class="text-subtitle-2 font-weight-black mb-3">Record Snapshot</div>
              <v-row dense class="border rounded ma-0 mb-6">
                <v-col
                  v-for="item in getStructuredData(selectedAuditLog.to || selectedAuditLog.from)"
                  :key="item.key"
                  cols="12"
                  sm="4"
                  class="pa-3 border-b border-r"
                >
                  <div class="text-caption grey--text font-weight-bold">
                    {{ formatFieldName(item.key) }}
                  </div>
                  <div class="text-body-2">{{ formatDisplayValue(item.value) }}</div>
                </v-col>
              </v-row>
            </div>
          </div>

          <!-- Evidence Display (All Case Evidence) -->
          <div v-if="allAttachments.length > 0" class="mt-8">
            <div class="text-subtitle-1 font-weight-black mb-4 d-flex align-center primary--text">
              <v-icon color="primary" class="mr-2">mdi-camera-outline</v-icon>
              All Audit Evidence
            </div>
            <v-row>
              <v-col v-for="(att, attIdx) in allAttachments" :key="attIdx" cols="12" sm="6" md="4">
                <v-card outlined class="attachment-card elevation-1 overflow-hidden">
                  <div class="image-container position-relative">
                    <v-img
                      :src="s3BaseUrl + att.fileUrl"
                      height="440"
                      contain
                      class="bg-grey-lighten-4 cursor-zoom-in"
                      @click="openDocument(s3BaseUrl + att.fileUrl)"
                    >
                      <!-- Action Tag Overlay -->
                      <div v-if="getCorrelatedAction(att)" class="image-overlay-tag">
                        <v-chip small color="primary" class="font-weight-black px-2 shadow-sm">
                          #{{ getCorrelatedAction(att).sequence }}
                          {{ getCorrelatedAction(att).name }}
                        </v-chip>
                      </div>
                    </v-img>
                  </div>

                  <div class="pa-3 bg-white border-t d-flex justify-space-between align-center">
                    <div class="text-caption font-weight-black grey--text text--darken-3">
                      <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                      {{ $dayjs(att.lastModified).format('DD MMM, hh:mm A') }}
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <div
            v-else-if="!loadingDocs"
            class="text-center pa-12 grey lighten-5 rounded border-dashed mt-4"
          >
            <v-icon size="64" color="grey lighten-2">mdi-image-multiple-outline</v-icon>
            <div class="text-h6 grey--text mt-4">No audit images found for this loan.</div>
            <div class="text-body-2 grey--text">
              Actions are tracked, but no evidence was captured.
            </div>
          </div>

          <v-progress-linear
            v-if="loadingDocs"
            indeterminate
            color="primary"
            class="mt-4"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { axiosUrl } from '@/utils/workforceApi'

export default {
  name: 'AuditTable',
  props: {
    caseConfig: Object
  },
  data() {
    return {
      loading: false,
      auditLogs: [],
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      headers: [],
      loanHeaders: [
        { text: 'Case/Loan No', value: 'loanNo', sortable: true },
        { text: 'Action', value: 'action', sortable: false },
        { text: 'Performed By', value: 'whoChanged', sortable: false },
        { text: 'Date & Time', value: 'createdAt', sortable: true },
        { text: 'Remarks', value: 'remarks', sortable: true }
      ],
      selectedLoan: null,
      detailsDialog: false,
      selectedAudit: null,
      selectedAuditIndex: 0,
      loadingDocs: false,
      documents: [],
      selectedDateIndex: 0,
      selectedAttachmentIndex: 0,
      s3BaseUrl: process.env.VUE_APP_S3_BASE_URL || ''
    }
  },
  computed: {
    availableDates() {
      if (!this.selectedLoan) return []
      const dates = this.selectedLoan.allAudits.map((log) =>
        this.$dayjs(log.createdAt).format('YYYY-MM-DD')
      )
      return Array.from(new Set(dates)).sort((a, b) => new Date(b) - new Date(a))
    },
    filteredAudits() {
      if (!this.selectedLoan || !this.availableDates[this.selectedDateIndex]) return []
      const date = this.availableDates[this.selectedDateIndex]
      return this.selectedLoan.allAudits
        .filter((log) => this.$dayjs(log.createdAt).format('YYYY-MM-DD') === date)
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    },
    selectedAuditLog() {
      return this.filteredAudits[this.selectedAuditIndex] || null
    },
    allAttachments() {
      if (!this.documents) return []
      const all = []
      this.documents.forEach((group) => {
        if (group.attachments) {
          all.push(...group.attachments)
        }
      })
      // Sort all attachments by last modified time descending
      return all.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))
    },
    currentAttachments() {
      if (!this.availableDates[this.selectedDateIndex]) return []
      const date = this.availableDates[this.selectedDateIndex]
      const group = this.documents.find((g) => g.date === date)
      return group ? group.attachments : []
    },
    groupedLoanList() {
      const map = new Map()
      this.auditLogs.forEach((log) => {
        const id = log.caseId?._id || log.caseId || log.recordId
        if (!map.has(id)) {
          map.set(id, {
            ...log,
            loanNo: this.getIdentificationNumber(log),
            lastAction: log.action,
            lastUpdatedAt: log.createdAt,
            allAudits: [log]
          })
        } else {
          const entry = map.get(id)
          // Update recent action if this log is newer
          if (new Date(log.createdAt) > new Date(entry.lastUpdatedAt)) {
            entry.lastAction = log.action
            entry.lastUpdatedAt = log.createdAt
            entry.whoChanged = log.whoChanged
            entry.remarks = log.remarks
          }
          entry.allAudits.push(log)
        }
      })
      return Array.from(map.values())
    },
    selectedImage() {
      return this.currentAttachments[this.selectedAttachmentIndex] || null
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchAudits()
      },
      deep: true
    },
    '$route.params.subCase': {
      handler() {
        this.fetchAudits()
      }
    }
  },
  methods: {
    async fetchAudits() {
      this.loading = true
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options
        const subCaseId = this.$route.params.subCase

        const params = {
          page,
          limit: itemsPerPage,
          sortBy: sortBy && sortBy[0],
          sortOrder: sortDesc && sortDesc[0] ? 'desc' : 'asc',
          subCase: subCaseId
        }

        const response = await this.$axios.get(axiosUrl.cases.getAuditLogs, { params })
        const responseData = response.data
        const logs = responseData?.data || []

        // Add identification number to each log
        this.auditLogs = logs.map((log) => ({
          ...log,
          loanNo: this.getIdentificationNumber(log)
        }))

        this.totalItems =
          responseData?.total !== undefined ? responseData.total : this.auditLogs.length
      } catch (error) {
        console.error('Audit Error:', error)
      } finally {
        this.loading = false
      }
    },
    getIdentificationNumber(item) {
      if (!item) return '-'
      if (item.caseId && typeof item.caseId === 'object' && item.caseId.caseNo)
        return item.caseId.caseNo
      if (item.loanId && typeof item.loanId === 'object' && item.loanId.loanNo)
        return item.loanId.loanNo

      const toObj = item.to || {}
      return toObj.caseNo || toObj.applicationNumber || toObj.loanNo || item.recordId || '-'
    },
    getActionColor(action) {
      const colors = {
        CREATE: 'success',
        UPDATE: 'info',
        DELETE: 'error'
      }
      return colors[action] || 'grey'
    },
    formatDate(date) {
      if (!date) return '-'
      return this.$dayjs(date).format('DD-MM-YYYY hh:mm A')
    },
    formatFieldName(field) {
      if (!field) return '-'
      return field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    },
    formatDisplayValue(val) {
      if (val === null || val === undefined) return '-'
      if (typeof val === 'boolean') return val ? 'Yes' : 'No'
      if (typeof val === 'object') {
        if (Array.isArray(val)) return `${val.length} Items`
        return val.name || val.fullName || val.label || 'Details'
      }
      return val
    },
    formatChipDate(date) {
      if (!date) return '-'
      return this.$dayjs(date).format('DD MMM, YYYY')
    },
    getCustomerName(item) {
      if (!item) return '-'
      const caseId = item.caseId || {}
      if (caseId.customer && typeof caseId.customer === 'object') {
        return (
          `${caseId.customer.firstName || ''} ${caseId.customer.lastName || ''}`.trim() ||
          'Anonymous'
        )
      }
      return 'N/A'
    },
    getParsedChanges(item) {
      if (!item || item.action !== 'UPDATE') return []

      // Handle explicit fieldChanged property
      if (item.fieldChanged && (item.from !== undefined || item.to !== undefined)) {
        return [{ key: item.fieldChanged, from: item.from, to: item.to }]
      }

      // Handle raw object comparison
      const fromObj = item.from || {}
      const toObj = item.to || {}

      if (typeof fromObj === 'object' && typeof toObj === 'object') {
        const keys = Array.from(new Set([...Object.keys(fromObj), ...Object.keys(toObj)]))
        return keys
          .filter((k) => !k.startsWith('_') && k !== 'updatedAt' && k !== 'createdAt')
          .map((k) => ({
            key: k,
            from: fromObj[k],
            to: toObj[k]
          }))
          .filter((c) => {
            const f = c.from === null || c.from === undefined ? '' : JSON.stringify(c.from)
            const t = c.to === null || c.to === undefined ? '' : JSON.stringify(c.to)
            return f !== t
          })
      }
      return []
    },
    getStructuredData(obj) {
      if (!obj || typeof obj !== 'object') return []
      const ignore = [
        '_id',
        '__v',
        'updatedAt',
        'createdAt',
        'createdBy',
        'updatedBy',
        'loanDisbursementsData',
        'loanWorkflowData',
        'emiWorkflowData'
      ]
      return Object.keys(obj)
        .filter((k) => !ignore.includes(k) && obj[k] !== null && obj[k] !== undefined)
        .map((k) => ({ key: k, value: obj[k] }))
    },
    async fetchDocuments(item) {
      const docId = item.caseId?._id || item.caseId
      if (!docId) return

      this.loadingDocs = true
      this.documents = []
      this.selectedDateIndex = 0
      this.selectedAttachmentIndex = 0

      try {
        const params = {
          docId: docId,
          useCase: this.caseConfig?.useCase || 'loan_management'
        }
        const response = await this.$axios.get(axiosUrl.cases.uploadCaseDocuments, { params })
        this.documents = response.data?.data || []
      } catch (error) {
        console.error('Error fetching documents:', error)
      } finally {
        this.loadingDocs = false
      }
    },
    showDetails(item) {
      // Find all audits for this specific case to show in the dialog history
      const caseId = item.caseId?._id || item.caseId || item.recordId
      const allAuditsForCase = this.auditLogs.filter((log) => {
        const logId = log.caseId?._id || log.caseId || log.recordId
        return logId === caseId
      })

      this.selectedLoan = {
        ...item,
        allAudits: allAuditsForCase.length > 0 ? allAuditsForCase : [item]
      }
      this.detailsDialog = true
      this.fetchDocuments(item)
    },
    getAuditSequence(log) {
      if (!log || !this.selectedLoan) return '-'
      const sorted = [...this.selectedLoan.allAudits].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      )
      return sorted.findIndex((l) => l._id === log._id) + 1
    },
    getCorrelatedAction(att) {
      if (!att || !this.selectedLoan) return null

      const imageTime = this.$dayjs(att.lastModified)

      const matchingAudit = this.selectedLoan.allAudits.find((log) => {
        const logTime = this.$dayjs(log.createdAt)
        return Math.abs(imageTime.diff(logTime, 'second')) < 60
      })

      if (matchingAudit) {
        return {
          sequence: this.getAuditSequence(matchingAudit),
          name: this.getAuditActionName(matchingAudit)
        }
      }
      return null
    },
    getAuditActionName(item) {
      if (!item) return '-'
      const action = item.lastAction || item.action
      if (action === 'CREATE') return 'Loan Creation'
      if (action === 'DELETE') return 'Delete Loan'
      if (action === 'UPDATE') {
        if (item.fieldChanged) return `Update ${this.formatFieldName(item.fieldChanged)}`
        return 'Update Loan'
      }
      return action
    },
    openDocument(url) {
      if (!url) return
      window.window.open(url, '_blank')
    }
  },
  mounted() {
    this.fetchAudits()
  }
}
</script>

<style scoped>
.custom-audit-table >>> th {
  background-color: #f5f5f5 !important;
  color: #333 !important;
  font-weight: bold !important;
}

.border-light {
  border: 1px solid #e0e0e0 !important;
}

.border {
  border: 1px solid #e0e0e0 !important;
}

.diff-box {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  line-height: 1.4;
  word-break: break-all;
}
.diff-box.old {
  background-color: #fff5f5;
  color: #c53030;
  text-decoration: line-through;
}
.diff-box.new {
  background-color: #f0fff4;
  color: #276749;
  font-weight: bold;
}
.border-b {
  border-bottom: 1px solid #e0e0e0 !important;
}
.border-l {
  border-left: 1px solid #e0e0e0 !important;
}
.border-r {
  border-right: 1px solid #e0e0e0 !important;
}
.border-primary {
  border: 1px solid #1976d2 !important;
}
.image-overlay-tag {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
}
.position-relative {
  position: relative;
}
.shadow-sm {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
}
.attachment-card {
  transition: all 0.3s;
}
.attachment-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15) !important;
}
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.border-dashed {
  border: 2px dashed #e0e0e0 !important;
}
.cursor-zoom-in {
  cursor: zoom-in;
}
</style>
